/*
 * Fantasy Football PDP behaviour. Loaded with `defer` after wtf-ff-shared.js.
 * Does not touch cart/AddToCart events directly. The sticky button proxies a
 * click to the real `button[name="add"]` only once a size is chosen, so
 * exactly one AddToCart event ever fires.
 *
 * 2026-10-09 (R543): tapping "Choose a size" (sticky bar, or the in-form button while no size is chosen) opens the size
 * sheet in assets/wtf-size-sheet.js when it is loaded and the screen is a phone. Without it, or above 749 px, the old
 * behaviour stays: scroll to the size pills. The Add proxy and the "adding" state live here too (see proxyAdd, setStickyBusy).
 *
 * Craft 16 replaces the whole <variant-selects> element on every option
 * change (confirmed by inspection, not assumed) — a brand-new element, not a
 * patch of the old one. That means:
 *   - "size chosen" state lives in a JS variable, never read back from the
 *     DOM (a freshly rendered radio can come back checked even though the
 *     shopper never chose a size).
 *   - anything cloned INTO the old <variant-selects> subtree (like the size
 *     chart trigger, in the Size legend) is destroyed with it. The clone
 *     source lives OUTSIDE that subtree and is re-cloned after every
 *     re-render, never moved.
 */
(function () {
  'use strict';

  var sizeChosen = false;
  var stickySync = null; // set once initStickyAtc runs
  var stickyBusySetter = null; // set once initStickyAtc runs (R543 fix 6)
  var CHOOSE_A_SIZE_LABEL = 'Choose a size';

  function track(name, data) {
    if (typeof window.wtfFfTrack === 'function') {
      window.wtfFfTrack(name, data || {});
    }
  }

  /* 2026-10-14 (speed build 1, audit 13): ONE MutationObserver on the product-info column for every callback in this file.
     Before: the size gate and the sticky bar each had their own observer on the same column, and both ran on every batch.
     Now one observer, one requestAnimationFrame per batch (still before the next paint), callbacks run in registration order. */
  var infoCbs = [];
  var infoMo = null;
  var infoRaf = 0;
  function onInfoChange(fn) {
    infoCbs.push(fn);
    if (infoMo || !('MutationObserver' in window)) return;
    var host = document.querySelector('.product__info-container') || document.querySelector('.product__info-wrapper');
    if (!host) return;
    infoMo = new MutationObserver(function () {
      if (infoRaf) return;
      infoRaf = window.requestAnimationFrame(function () {
        infoRaf = 0;
        for (var i = 0; i < infoCbs.length; i++) infoCbs[i]();
      });
    });
    infoMo.observe(host, { childList: true, subtree: true });
  }

  function getRealForm() {
    return document.querySelector('form[data-type="add-to-cart-form"]');
  }

  function getRealButton() {
    var form = getRealForm();
    return form ? form.querySelector('button[name="add"]') : null;
  }

  function getPriceText() {
    var el = document.querySelector(
      '.product__info-wrapper .price .price-item--sale, .product__info-wrapper .price .price-item--regular'
    );
    return el ? el.textContent.trim() : '';
  }

  /* 2026-10-06 (R473 fix 1): the struck compare-at price, empty when the product is not on sale. */
  function getCompareText() {
    var el = document.querySelector(
      '.product__info-wrapper .price--on-sale .price__sale .price-item--regular'
    );
    return el ? el.textContent.trim() : '';
  }

  function getSizeFieldset() {
    var fieldsets = document.querySelectorAll('variant-selects fieldset');
    for (var i = 0; i < fieldsets.length; i++) {
      var legend = fieldsets[i].querySelector('legend');
      /* Prefix match, not equality: once the size-chart trigger clone lands
         inside this legend, its own text ("Size Chart") is appended to the
         legend's textContent, so an exact "Size" match would stop matching
         after the very first render. */
      if (legend && legend.textContent.trim().indexOf('Size') === 0) return fieldsets[i];
    }
    return null;
  }

  function getSizeRadios() {
    var fs = getSizeFieldset();
    return fs ? Array.prototype.slice.call(fs.querySelectorAll('input[type="radio"]')) : [];
  }

  function showSizeHint() {
    var h = document.querySelector('[data-wtf-ff-size-hint]');
    if (h) h.hidden = false;
  }

  function hideSizeHint() {
    var h = document.querySelector('[data-wtf-ff-size-hint]');
    if (h) h.hidden = true;
  }

  function getHintText() {
    var h = document.querySelector('[data-wtf-ff-size-hint]');
    var t = h ? h.textContent.trim() : '';
    return t || CHOOSE_A_SIZE_LABEL;
  }

  function enforceUnchosenLook() {
    getSizeRadios().forEach(function (r) {
      if (r.checked) r.checked = false;
    });
  }

  /* Judge fix (defect 1), 2026-09-19: mirror the sticky button's "Choose a
     size" label onto the real button[name="add"] itself while no size is
     chosen, then restore its true server-rendered label ("Add to cart" /
     "Sold out" / whichever the variant actually is) once one is. The real
     label is never assumed or hard-coded: it's captured off the live node's
     own span the first time it's overridden, so whatever Liquid/Craft put
     there for the current variant comes back unchanged. Stored on a data
     attribute (not a module-level variable) because a Craft re-render can
     hand back a brand-new button node with its own fresh real label — this
     function re-captures per node, never trusts a stale cached string. Only
     the span's text changes; the button's own `disabled` attribute and the
     capture-phase click gate below are untouched, so a size-less click still
     adds nothing and still moves focus to the size row. */
  function syncMainButtonLabel() {
    var realBtn = getRealButton();
    if (!realBtn) return;
    var labelSpan = realBtn.querySelector(':scope > span') || realBtn.querySelector('span');
    if (!labelSpan) return;
    /* Idempotency guard, load-bearing: `.textContent = value` always replaces
       the element's child Text node with a brand-new one, which is a
       childList mutation even when the string is unchanged. This function is
       called from a MutationObserver watching the same container (childList:
       true, subtree: true). Writing unconditionally re-triggers that same
       observer on every write, forever, pegging the tab (found live: the
       page went fully unresponsive, confirmed by isolating a bare
       `page.evaluate(() => 1 + 1)` hang after this label sync went live
       without the guard). Only write when the value would actually change. */
    if (!sizeChosen) {
      if (!('wtfFfRealLabel' in realBtn.dataset)) {
        /* 2026-10-14 (audit 3): when Liquid already printed "Choose a size", the true label rides on data-wtf-real-label. */
        realBtn.dataset.wtfFfRealLabel = labelSpan.getAttribute('data-wtf-real-label') || labelSpan.textContent;
      }
      if (labelSpan.textContent !== CHOOSE_A_SIZE_LABEL) {
        labelSpan.textContent = CHOOSE_A_SIZE_LABEL;
      }
    } else if ('wtfFfRealLabel' in realBtn.dataset) {
      var restoredLabel = realBtn.dataset.wtfFfRealLabel;
      delete realBtn.dataset.wtfFfRealLabel;
      if (labelSpan.textContent !== restoredLabel) {
        labelSpan.textContent = restoredLabel;
      }
    }
  }

  /* Dynamic checkout ("Buy with Shop" / "More payment options") bypasses the
     gate: it can buy the default (first available) variant with no size
     ever chosen, which is the exact failure the gate exists to stop.
     .shopify-payment-button, <product-form> and .product__info-container all
     survived a Craft variant re-render in testing (unlike <variant-selects>),
     so the class lives on .product__info-container — confirmed stable — and
     the CSS descendant selector re-applies automatically even if the button
     itself is ever regenerated. inert/aria-hidden are set directly on the
     button too, re-applied defensively on every observer tick. */
  function applyDynamicCheckoutGate() {
    var container = document.querySelector('.product__info-container') || document.querySelector('.product__info-wrapper');
    if (container) {
      container.classList.toggle('wtf-ff-size-not-chosen', !sizeChosen);
    }
    var pb = document.querySelector('.shopify-payment-button');
    if (pb) {
      if (!sizeChosen) {
        pb.setAttribute('inert', '');
        pb.setAttribute('aria-hidden', 'true');
      } else {
        pb.removeAttribute('inert');
        pb.removeAttribute('aria-hidden');
      }
    }
  }

  function focusSizeFieldset() {
    var fs = getSizeFieldset();
    if (!fs) return;
    fs.scrollIntoView({ behavior: 'smooth', block: 'center' });
    var firstRadio = fs.querySelector('input[type="radio"]');
    /* 2026-10-04 (batch B, row 2): preventScroll, so the focus call cannot cancel the smooth scroll to the size pills above. */
    if (firstRadio) { try { firstRadio.focus({ preventScroll: true }); } catch (e) { firstRadio.focus(); } }
    showSizeHint();
  }

  /* R543 fix 1: a phone opens the size sheet; anything else (no sheet script, wide screen, no option groups) keeps the old scroll. */
  function openSizeSheet(from) {
    if (window.wtfSizeSheet && typeof window.wtfSizeSheet.open === 'function') {
      var opened = false;
      try {
        opened = window.wtfSizeSheet.open(from);
      } catch (e) {
        opened = false;
      }
      if (opened) return;
    }
    focusSizeFieldset();
  }

  /* R543 fix 6: the one place that clicks the real Add button on behalf of the sticky bar and the size sheet.
     The sticky button shows its busy state in the same tick. If the real click starts no request (a gate or a
     custom-field check stopped it), the busy state is cleared at once. The cart request itself is not changed. */
  function proxyAdd() {
    var realBtn = getRealButton();
    if (!realBtn || realBtn.disabled) return;
    if (stickyBusySetter) stickyBusySetter(true);
    realBtn.click();
    if (!realBtn.classList.contains('loading') && stickyBusySetter) stickyBusySetter(false);
  }

  /* Re-clone the size-chart trigger into the current Size legend if it is
     missing there (true on first load, and true again after every Craft
     re-render, since the clone lives inside the replaced subtree). */
  function ensureSizeChartClone() {
    var source = document.querySelector('[data-wtf-ff-sizechart-source]');
    if (!source) return;
    var fs = getSizeFieldset();
    if (!fs) return;
    var legend = fs.querySelector('legend');
    if (!legend) return;
    legend.classList.add('wtf-ff-size-legend-row');
    if (legend.querySelector('.wtf-ff-sizechart-opener--clone')) return;
    /* Bug found in QA: after the first clone, `source` itself gets marked
       --hidden-source (right below). cloneNode(true) copies whatever class
       list `source` carries AT CLONE TIME, so every clone made after the
       first re-render inherited --hidden-source and rendered display:none —
       the trigger vanished the moment Craft's first re-render destroyed the
       previous clone and this function ran again. Strip both source-only
       classes from the clone explicitly; never rely on which classes
       `source` already had. */
    var clone = source.cloneNode(true);
    clone.classList.remove('wtf-ff-sizechart-opener--source', 'wtf-ff-sizechart-opener--hidden-source');
    clone.classList.add('wtf-ff-sizechart-opener--clone', 'wtf-ff-sizechart-opener--inline');
    clone.removeAttribute('data-wtf-ff-sizechart-source');
    legend.appendChild(clone);
    source.classList.add('wtf-ff-sizechart-opener--hidden-source');
  }

  /* QA fix, 2026-09-10: some size charts (Vikings, 9 columns) still don't
     fit the dialog even after the tighter type/padding in wtf-ff-pdp.css.
     Measured only after the dialog has had a layout pass (a fixed delay
     after the opening click, not on load, since the table is hidden and
     has no real width until the dialog is shown), and only flagged when it
     actually overflows — never guessed from column count. */
  function checkSizeTableOverflow() {
    var wrap = document.querySelector('.wtf-ff-size-dialog__table');
    if (!wrap) return;
    /* Judge-qa fix, 2026-09-10: the wrapper alone was measured before, but
       below 750px the theme's own rule (section-main-product.css) gives the
       <table> a second, independent scroll container of its own
       (display:block; overflow-x:auto), so the TABLE clips its content
       before the wrapper ever sees an overflow — the wrapper's scrollWidth
       cannot exceed its clientWidth in that state, so this check could
       never go red. Measure both; either one overflowing counts. */
    var table = wrap.querySelector('table');
    var wrapOverflow = wrap.scrollWidth > wrap.clientWidth + 1;
    var tableOverflow = table ? table.scrollWidth > table.clientWidth + 1 : false;
    var overflowing = wrapOverflow || tableOverflow;
    wrap.classList.toggle('wtf-ff-size-dialog__table--scrolls', overflowing);
  }

  function wireSizeChartAnalytics() {
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-wtf-ff-sizechart-open]');
      if (!trigger) return;
      track('size_chart_open', {
        product_id: trigger.getAttribute('data-product-id') || '',
        product_title: trigger.getAttribute('data-product-title') || '',
        team: document.body.getAttribute('data-wtf-ff-team') || ''
      });
      window.setTimeout(checkSizeTableOverflow, 150);
    });
  }

  /* Judge-qa fix, 2026-09-10 (3rd round): the mobile bottom sheet draws
     under the Klaviyo hub launcher (black chat bubble) and the Smile.io
     rewards pill — both position:fixed, both higher z-index than the sheet,
     neither dimmed by its backdrop. They covered size-chart text on every
     product. A MutationObserver on the dialog's own `open` attribute is the
     single source of truth for "is it open" — it fires no matter which path
     closed it (close button, Escape, backdrop click, or any future one),
     so there is nothing else to wire up. This is a SEPARATE class from the
     sticky-bar lift class below; it does not reuse or edit that rule. */
  function watchSizeDialogOpenState() {
    var dlg = document.querySelector('.wtf-ff-size-dialog');
    if (!dlg) return;
    function sync() {
      document.body.classList.toggle('wtf-ff-size-dialog-open', dlg.hasAttribute('open'));
    }
    sync();
    if ('MutationObserver' in window) {
      new MutationObserver(sync).observe(dlg, { attributes: true, attributeFilter: ['open'] });
    }
  }

  /* 7.2 "Size required" gate. Track state in JS only; never trust a checked
     radio to mean the shopper chose it, because Craft can re-render the
     picker with a size checked (the server's computed default for whatever
     color/variant is current) even when nothing was clicked. */
  /* 2026-10-14 (audit 3): Liquid prints "Choose a size" on the Add button only when it expects this script to gate the size.
     Where the script does not gate (an unusable ?variant= value, no Size fieldset), put the true label back. */
  function restoreServerLabel() {
    var realBtn = getRealButton();
    var labelSpan = realBtn ? realBtn.querySelector('span') : null;
    var real = labelSpan ? labelSpan.getAttribute('data-wtf-real-label') : '';
    if (real && labelSpan.textContent !== real) labelSpan.textContent = real;
  }

  function initSizeGate() {
    var params = new URLSearchParams(window.location.search);
    if (params.has('variant')) {
      /* 2026-10-14 (judge1 fix): a ?variant= value counts as "size chosen" only when a Size input is really checked (a valid variant:
         Liquid keeps that variant's size checked) or the page has no Size input. An invalid value (?variant=1) leaves no size checked,
         so the gate stays on and the Add button keeps the Liquid label "Choose a size". */
      var urlSizeRadios = getSizeRadios();
      var urlSizeChecked = urlSizeRadios.some(function (r) { return r.checked; });
      if (urlSizeRadios.length === 0 || urlSizeChecked) {
        sizeChosen = true;
        restoreServerLabel();
        return;
      }
    }

    var fs = getSizeFieldset();
    if (!fs) {
      /* No Size option on this product: nothing to gate. */
      sizeChosen = true;
      restoreServerLabel();
      return;
    }

    enforceUnchosenLook();
    ensureSizeChartClone();
    applyDynamicCheckoutGate();
    syncMainButtonLabel();

    document.addEventListener(
      'change',
      function (event) {
        var radio = event.target;
        if (!radio || radio.tagName !== 'INPUT' || radio.type !== 'radio') return;
        var radioFs = radio.closest('fieldset');
        var legend = radioFs && radioFs.querySelector('legend');
        if (legend && legend.textContent.trim().indexOf('Size') === 0) {
          sizeChosen = true;
          hideSizeHint();
          applyDynamicCheckoutGate();
          syncMainButtonLabel();
          if (stickySync) stickySync();
        }
      },
      true
    );

    onInfoChange(function () {
      if (!sizeChosen) enforceUnchosenLook();
      ensureSizeChartClone();
      applyDynamicCheckoutGate();
      syncMainButtonLabel();
    });

    /* Capture phase: run before Craft's own submit handling and before the
       browser would otherwise submit the form. */
    document.addEventListener(
      'click',
      function (event) {
        var btn = event.target.closest('button[name="add"]');
        if (!btn) return;
        if (sizeChosen) return;
        event.preventDefault();
        event.stopPropagation();
        if (event.stopImmediatePropagation) event.stopImmediatePropagation();
        syncMainButtonLabel();
        openSizeSheet(btn);
      },
      true
    );
  }

  /* 7.4 Sticky ATC. Appended to document.body on init so it is never a
     descendant of the .product__info-wrapper.scroll-trigger.animate--slide-in
     ancestor, whose transform makes position:fixed resolve against that
     column instead of the viewport (BUILD-SPEC.md section 8A). */
  function initStickyAtc() {
    var bar = document.querySelector('.wtf-ff-sticky-atc');
    if (!bar) return;

    document.body.appendChild(bar);
    bar.setAttribute('aria-hidden', 'true');

    var summaryEl = bar.querySelector('.wtf-ff-sticky-atc__summary');
    var btn = bar.querySelector('.wtf-ff-sticky-atc__btn');
    var btnLabel = btn ? btn.querySelector('span') : null;

    /* 2026-10-06 (R473 fix 1): chosen option values only ("Navy", then "Navy · M" once a size is chosen).
       The price is its own line and always shows, with or without a size. */
    function getSelectedSummary() {
      var checked = document.querySelectorAll('variant-selects input:checked');
      var values = Array.prototype.map.call(checked, function (input) {
        return input.value;
      });
      return values.join(' · ');
    }

    function renderSummary() {
      if (!summaryEl) return;
      while (summaryEl.firstChild) summaryEl.removeChild(summaryEl.firstChild);
      var price = getPriceText();
      if (price) {
        var priceEl = document.createElement('span');
        priceEl.className = 'wtf-ff-sticky-atc__price';
        var compare = getCompareText();
        if (compare) {
          var strike = document.createElement('s');
          strike.textContent = compare;
          priceEl.appendChild(strike);
        }
        priceEl.appendChild(document.createTextNode(price));
        summaryEl.appendChild(priceEl);
      }
      var opts = getSelectedSummary();
      if (opts) {
        var optsEl = document.createElement('span');
        optsEl.className = 'wtf-ff-sticky-atc__opts';
        optsEl.textContent = opts;
        summaryEl.appendChild(optsEl);
      }
    }

    /* The phone strip above the image (.wtf-pdp-top in the back-link section) renders the first variant's
       price on the server. 236 of 245 active products price some sizes differently, so the strip is
       re-synced from the main price block on every variant change. */
    function syncTopStrip() {
      var strip = document.querySelector('[data-wtf-pdp-top-price]');
      var price = getPriceText();
      if (!strip || !price) return;
      var compare = getCompareText();
      /* 2026-10-14 (audit 24): same text as now on screen = do nothing (the old code deleted and rebuilt the strip every sync). */
      var curNow = strip.querySelector('.wtf-pdp-top__now');
      var curCmp = strip.querySelector('.wtf-pdp-top__compare');
      if (curNow && curNow.textContent.trim() === price && (curCmp ? curCmp.textContent.trim() : '') === compare) return;
      while (strip.firstChild) strip.removeChild(strip.firstChild);
      if (compare) {
        var s = document.createElement('s');
        s.className = 'wtf-pdp-top__compare';
        s.textContent = compare;
        strip.appendChild(s);
      }
      var now = document.createElement('span');
      now.className = 'wtf-pdp-top__now';
      now.textContent = price;
      strip.appendChild(now);
    }

    function syncSticky() {
      renderSummary();
      syncTopStrip();
      if (!btn) return;
      if (!sizeChosen) {
        btn.disabled = false;
        if (btnLabel) btnLabel.textContent = 'Choose a size';
        return;
      }
      var realBtn = getRealButton();
      if (realBtn) {
        btn.disabled = !!realBtn.disabled;
        var realSpan = realBtn.querySelector('span');
        if (btnLabel && realSpan && realSpan.textContent.trim()) {
          btnLabel.textContent = realSpan.textContent.trim();
        }
      }
    }
    stickySync = syncSticky;

    /* R543 fix 6: busy state for the sticky button. A ring replaces the label (CSS .wtf-ff-busy). It follows the real
       button's own `loading` class, which assets/product-form.js sets on submit and clears when the request ends. */
    var busyRing = null;
    function setStickyBusy(on) {
      if (!btn) return;
      if (on && !busyRing) {
        busyRing = document.createElement('span');
        busyRing.className = 'wtf-busy-ring';
        busyRing.setAttribute('aria-hidden', 'true');
        btn.appendChild(busyRing);
      }
      btn.classList.toggle('wtf-ff-busy', !!on);
      if (on) {
        btn.setAttribute('aria-busy', 'true');
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.removeAttribute('aria-busy');
        btn.removeAttribute('aria-disabled');
      }
    }
    stickyBusySetter = setStickyBusy;

    var busyNode = null;
    var busyObserver = null;
    function syncBusy() {
      var rb = getRealButton();
      var on = !!(rb && rb.classList.contains('loading'));
      if (rb) rb.setAttribute('aria-busy', on ? 'true' : 'false');
      setStickyBusy(on);
    }
    function watchBusy() {
      var rb = getRealButton();
      if (!rb || rb === busyNode) return;
      busyNode = rb;
      if (busyObserver) busyObserver.disconnect();
      if ('MutationObserver' in window) {
        busyObserver = new MutationObserver(syncBusy);
        busyObserver.observe(rb, { attributes: true, attributeFilter: ['class'] });
      }
    }
    watchBusy();

    if (btn) {
      btn.addEventListener('click', function () {
        if (btn.getAttribute('aria-busy') === 'true') return;
        if (!sizeChosen) {
          openSizeSheet(btn);
          return;
        }
        proxyAdd();
      });
    }

    document.addEventListener('change', function (event) {
      if (event.target.closest('variant-selects')) {
        window.setTimeout(syncSticky, 60);
      }
    });

    /* 2026-10-04 (batch B, row 2): the bar shows from scroll 0 and stays until the REAL Add button is in view.
       Before: it waited until the form had scrolled past the top (1,200-1,500 px down on a phone), so a shopper
       at scroll 0 had no Add button on the first screen. The button is watched, not the form. The observer's
       bottom margin is the bar's own height, so a button hidden under the bar counts as not in view.
       A Craft re-render can swap the button node: watchAddButton() re-observes when the node changes. */
    var watchAddButton = null;
    var realForm = getRealForm();
    if (realForm && 'IntersectionObserver' in window) {
      var addInView = true; /* treated as in view until the first observer callback */
      var footerVisible = false;
      var footerEl = document.querySelector('.footer');
      var addTarget = null;
      var addObserver = null;
      var barOffset = Math.max(Math.round(bar.offsetHeight) || 0, 66);

      function updateVisibility() {
        var shouldShow = !addInView && !footerVisible;
        bar.classList.toggle('wtf-ff-sticky-atc--visible', shouldShow);
        bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
        /* Lift the Klaviyo and Smile.io floating launchers above the bar
           while it's visible so it doesn't overlap them; they return to
           their own default position the moment this class is removed. */
        if (shouldShow) {
          var h = bar.getBoundingClientRect().height;
          if (h > 0) document.body.style.setProperty('--wtf-ff-sticky-h', h + 'px');
        }
        document.body.classList.toggle('wtf-ff-sticky-launchers-lift', shouldShow);
      }

      watchAddButton = function () {
        var node = getRealButton() || getRealForm();
        if (!node || node === addTarget) return;
        addTarget = node;
        if (addObserver) addObserver.disconnect();
        addObserver = new IntersectionObserver(
          function (entries) {
            addInView = entries[entries.length - 1].isIntersecting;
            updateVisibility();
          },
          { threshold: 0, rootMargin: '0px 0px -' + barOffset + 'px 0px' }
        );
        addObserver.observe(node);
      };
      watchAddButton();

      if (footerEl) {
        new IntersectionObserver(
          function (entries) {
            footerVisible = entries[0].isIntersecting;
            updateVisibility();
          },
          { threshold: 0 }
        ).observe(footerEl);
      }
    }

    syncSticky();

    /* Also resync after any Craft re-render (color change, etc.) even
       without a Size-fieldset change event, so the price/color summary
       stays correct. */
    var syncTimer = 0;
    onInfoChange(function () {
      if (syncTimer) window.clearTimeout(syncTimer);
      syncTimer = window.setTimeout(function () {
        syncTimer = 0;
        syncSticky();
      }, 60);
      if (watchAddButton) watchAddButton();
      watchBusy();
    });
  }

  /* R543: read-only state and the Add proxy, for assets/wtf-size-sheet.js. */
  window.wtfFfPdp = {
    isSizeChosen: function () {
      return sizeChosen;
    },
    getRealButton: getRealButton,
    getPriceText: getPriceText,
    getCompareText: getCompareText,
    getHintText: getHintText,
    addToCart: proxyAdd
  };

  function init() {
    initSizeGate();
    wireSizeChartAnalytics();
    watchSizeDialogOpenState();
    initStickyAtc();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
